import { getTheme, mergeStyleSets } from '@fluentui/react';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { IPText } from 'baseComponents/Text/IPText';
import { getEntityTypeName } from 'globalFunctions';
import { mapFromCopilotContextProposalPayload } from 'mappings/copilotMapping';
import { CopilotConversationContentBlock } from 'models/copilot/copilotConversation';
import { EntityTypes } from 'models/entity';
import { useTranslation } from 'react-i18next';

interface ICopilotContextProposalCardProps {
  block: CopilotConversationContentBlock;
}

// A ContextProposal block: the relations the task assistant added to the open task, each with its reason, and the
// candidates it left out, muted, with why. The card is the record; the task modal applied the items when the block
// arrived. A payload that is no proposal, or an empty one, renders nothing.
export const CopilotContextProposalCard = (props: ICopilotContextProposalCardProps) => {
  const { t } = useTranslation(['copilot', 'translation']);
  const theme = getTheme();
  const proposal = mapFromCopilotContextProposalPayload(props.block.payload);
  if (!proposal || (proposal.items.length === 0 && proposal.refused.length === 0)) return null;

  const styles = mergeStyleSets({
    card: {
      border: `1px solid ${theme.palette.neutralTertiaryAlt}`,
      borderRadius: 4,
      background: theme.palette.white,
      padding: '6px 10px',
    },
    header: {
      fontWeight: 600,
    },
    name: {
      color: theme.palette.neutralPrimary,
    },
    reason: {
      color: theme.palette.neutralSecondary,
    },
    muted: {
      color: theme.semanticColors.disabledText,
    },
  });

  const kindLabel = (entityType: EntityTypes | undefined) =>
    entityType === undefined ? '' : getEntityTypeName(entityType, t);

  return (
    <IPStack className={styles.card} tokens={{ childrenGap: 6 }}>
      {proposal.items.length > 0 && (
        <IPStack.Item>
          <IPText className={styles.header}>{t('copilot:TaskAssistant.ContextCard.Added')}</IPText>
        </IPStack.Item>
      )}
      {proposal.items.map((item) => (
        <IPStack.Item key={`${item.entityType}-${item.entityId}`}>
          <IPStack tokens={{ childrenGap: 2 }}>
            <IPText className={styles.name}>
              {`${kindLabel(item.entityType)}: ${item.code ? `${item.code} ` : ''}${item.name}`}
            </IPText>
            {item.reason && (
              <IPText variant="small" className={styles.reason}>
                {item.reason}
              </IPText>
            )}
          </IPStack>
        </IPStack.Item>
      ))}
      {proposal.refused.length > 0 && (
        <IPStack.Item>
          <IPText className={`${styles.header} ${styles.muted}`}>
            {t('copilot:TaskAssistant.ContextCard.Refused')}
          </IPText>
        </IPStack.Item>
      )}
      {proposal.refused.map((refusal, index) => (
        <IPStack.Item key={`refused-${index}`}>
          <IPText variant="small" className={styles.muted}>
            {[refusal.entityType === undefined ? refusal.kind : kindLabel(refusal.entityType), refusal.name]
              .filter((part) => part)
              .join(': ')}
            {refusal.reason ? ` - ${refusal.reason}` : ''}
          </IPText>
        </IPStack.Item>
      ))}
    </IPStack>
  );
};
